import { useEffect, useRef, useState } from "react";
import { ConfirmModal } from "../components/ConfirmModal.jsx";
import { StopAnswerSheet } from "../components/games/StopAnswerSheet.jsx";
import { StopLetterDraw } from "../components/games/StopLetterDraw.jsx";
import {
  GoogleAccountButton,
  useSoloAuth,
} from "../contexts/SoloAuthContext.jsx";
import { SoundButton } from "../contexts/SoundContext.jsx";
import { STOP_CATEGORIES } from "../data/stopCategories.js";
import {
  contarRespostasStopValidas,
  respostasStopCompletas,
  respostaStopComecaComLetra,
  sortearLetraStop,
} from "../domain/stop.js";
import { useStopAnswers } from "../hooks/useStopAnswers.js";
import {
  registrarVitoriaStop,
  salvarRecordeStop,
} from "../services/ranking.js";

const SESSION_KEY = "@PartyGames:stopSoloSessao";
const LEGACY_SESSION_KEY = "@PartyGames:stopSoloEmAndamento";

const readSession = () => {
  try {
    const value = JSON.parse(localStorage.getItem(SESSION_KEY));
    return ["roleta", "jogando", "fim"].includes(value?.etapa) ? value : null;
  } catch {
    return null;
  }
};
const removeSession = () => {
  try {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LEGACY_SESSION_KEY);
  } catch {}
};

export function StopSoloPage({ aoVoltar }) {
  const { googleUser } = useSoloAuth();
  const restored = useRef(readSession()).current;
  const initialSeconds =
    restored?.etapa === "jogando"
      ? Math.max(
          0,
          Math.floor((Date.now() - (restored.inicioEm || Date.now())) / 1000),
        )
      : restored?.tempoFinal || 0;
  const [stage, setStage] = useState(restored?.etapa || "selecao");
  const [letter, setLetter] = useState(restored?.letra || "");
  const [seconds, setSeconds] = useState(initialSeconds);
  const secondsRef = useRef(initialSeconds);
  const startedAt = useRef(restored?.inicioEm || null);
  const [rankingSaved, setRankingSaved] = useState(
    Boolean(restored?.salvouRanking),
  );
  const [exitOpen, setExitOpen] = useState(false);
  const activeGame = useRef(stage === "jogando");
  const [roundId, setRoundId] = useState(restored?.sessaoId || 0);
  const engine = useStopAnswers({
    active: stage === "jogando",
    initialAnswers:
      roundId === restored?.sessaoId ? restored?.respostas || {} : {},
    resetKey: roundId,
  });
  const validAnswers = contarRespostasStopValidas(
    engine.answers,
    letter,
    STOP_CATEGORIES,
  );
  const complete = respostasStopCompletas(engine.answers, STOP_CATEGORIES);

  useEffect(() => {
    document.body.classList.toggle(
      "stop-active",
      stage === "roleta" || stage === "jogando",
    );
    return () => document.body.classList.remove("stop-active");
  }, [stage]);

  useEffect(() => {
    if (stage !== "jogando") return undefined;
    const interval = window.setInterval(() => {
      setSeconds((current) => {
        const next = current + 1;
        secondsRef.current = next;
        return next;
      });
    }, 1000);
    return () => window.clearInterval(interval);
  }, [stage]);

  useEffect(() => {
    try {
      if (["roleta", "jogando", "fim"].includes(stage)) {
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({
            etapa: stage,
            letra: letter,
            respostas: engine.answers,
            sessaoId: roundId,
            salvouRanking: rankingSaved,
            inicioEm: startedAt.current,
            tempoFinal: stage === "fim" ? secondsRef.current : null,
          }),
        );
        localStorage.removeItem(LEGACY_SESSION_KEY);
      } else removeSession();
    } catch {}
  }, [stage, letter, engine.answers, roundId, rankingSaved]);

  useEffect(() => {
    if (stage !== "fim" || !googleUser || rankingSaved) return;
    const hits = contarRespostasStopValidas(
      engine.answersRef.current,
      letter,
      STOP_CATEGORIES,
    );
    const operations = [
      salvarRecordeStop(googleUser, hits, secondsRef.current),
    ];
    if (hits === STOP_CATEGORIES.length) {
      operations.push(registrarVitoriaStop(googleUser));
    }
    Promise.all(operations)
      .then(() => setRankingSaved(true))
      .catch((error) =>
        console.error("Erro ao salvar ranking do Stop:", error),
      );
  }, [stage, googleUser, rankingSaved, letter]);

  const start = () => {
    const nextId = Date.now();
    setLetter(sortearLetraStop());
    setSeconds(0);
    secondsRef.current = 0;
    startedAt.current = null;
    setRankingSaved(false);
    activeGame.current = false;
    setRoundId(nextId);
    setStage("roleta");
  };

  const beginAnswers = () => {
    startedAt.current = Date.now();
    activeGame.current = true;
    setStage("jogando");
  };

  const finish = () => {
    if (!complete || !activeGame.current) return;
    activeGame.current = false;
    setStage("fim");
  };

  const leave = () => {
    removeSession();
    aoVoltar();
  };

  if (stage === "selecao") {
    return (
      <div className="container-sm text-center stop-solo-selection">
        <button className="stop-back" onClick={aoVoltar} type="button">
          ← Voltar
        </button>
        <h2 className="title">🛑 Stop Solo</h2>
        <p className="subtitle mb-40">
          Sorteie uma letra e preencha as {STOP_CATEGORIES.length} categorias o
          mais rápido que conseguir.
        </p>
        <GoogleAccountButton />
        <div className="card stop-solo-categories">
          <h3 className="section-title">Categorias desta partida:</h3>
          <div>
            {STOP_CATEGORIES.map((category) => (
              <span key={category.id}>
                {category.emoji} {category.label}
                {category.descricao ? ` — ${category.descricao}` : ""}
              </span>
            ))}
          </div>
          <button className="btn btn-primary" onClick={start} type="button">
            🎲 Sortear Letra e Começar
          </button>
        </div>
      </div>
    );
  }

  if (stage === "fim") {
    const perfect = validAnswers === STOP_CATEGORIES.length;
    return (
      <div className="container-sm text-center stop-solo-result">
        <div
          className={`card stop-solo-result-card ${perfect ? "perfect" : ""}`}
        >
          <h3>{perfect ? "🎉 Stop Perfeito!" : "🛑 Rodada Encerrada!"}</h3>
          <p className="stop-solo-summary">
            Letra <strong>{letter}</strong> —{" "}
            <strong>
              {validAnswers}/{STOP_CATEGORIES.length}
            </strong>{" "}
            categorias válidas, em <strong>{seconds}s</strong>
          </p>
          <div className="stop-solo-answer-result">
            {STOP_CATEGORIES.map((category) => {
              const answer = (engine.answers[category.id] || "").trim();
              const valid = respostaStopComecaComLetra(answer, letter);
              return (
                <div key={category.id}>
                  <span>
                    {category.emoji} {category.label}
                  </span>
                  <strong className={valid ? "valid" : "invalid"}>
                    {answer || "em branco"}
                  </strong>
                </div>
              );
            })}
          </div>
          {googleUser ? (
            rankingSaved && (
              <p className="info-box">🏆 Resultado salvo no seu Ranking!</p>
            )
          ) : (
            <div className="stop-login-prompt">
              <p>Entre com Google para salvar esse resultado no ranking:</p>
              <GoogleAccountButton />
            </div>
          )}
          <div className="stop-solo-actions">
            <button className="btn btn-primary" onClick={start} type="button">
              🔁 Jogar de Novo
            </button>
            <button
              className="btn stop-secondary"
              onClick={leave}
              type="button"
            >
              Sair
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stop-game-shell stop-solo-shell">
      <header className="stop-game-header stop-solo-header">
        <button
          className="stop-header-exit"
          onClick={() => setExitOpen(true)}
          type="button"
        >
          🚪 Sair
        </button>
        <div>
          <SoundButton variante="inline" />
          <h2>🛑 Stop Solo</h2>
        </div>
        <strong>⏱️ {seconds}s</strong>
      </header>
      {stage === "roleta" ? (
        <StopLetterDraw
          letraFinal={letter}
          duracaoMs={2500}
          aoTerminar={beginAnswers}
        />
      ) : (
        <StopAnswerSheet
          letter={letter}
          answers={engine.answers}
          activeIndex={engine.activeIndex}
          onActiveIndexChange={engine.setActiveIndex}
          onType={engine.typeKey}
          onStop={finish}
          canStop={complete}
        />
      )}
      <ConfirmModal
        isOpen={exitOpen}
        title="Sair da partida?"
        message="Seu progresso nessa rodada de Stop será perdido."
        confirmText="Sim, sair"
        onConfirm={() => {
          setExitOpen(false);
          leave();
        }}
        onCancel={() => setExitOpen(false)}
      />
    </div>
  );
}
